/**
 * SEO utility functions for managing meta tags, OpenGraph, and structured data
 */

/**
 * Update page meta tags for SEO
 * 
 * @param {Object} options - SEO options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.keywords - Meta keywords
 * @param {string} options.url - Canonical URL
 * @param {string} options.image - OpenGraph image URL
 * @param {string} options.type - OpenGraph type (website, article, etc.)
 */
export const updateMetaTags = (options) => {
  const defaults = {
    title: 'AtOnline - Enterprise Solutions',
    description: 'Enterprise-grade solutions for content management, file storage, global management, mailing lists, and telephony services.',
    keywords: 'content management, file storage, drive, kakari, mailing lists, telephony, enterprise solutions',
    url: window.location.pathname ? `https://www.atonline.com${window.location.pathname}` : 'https://www.atonline.com',
    image: 'https://www.atonline.com/img/logo_square_transp.png',
    type: 'website',
    siteName: 'AtOnline'
  }

  // Merge defaults with provided options
  const meta = { ...defaults, ...options }

  // Update document title
  document.title = meta.title

  // Helper function to create or update meta tags
  const setMetaTag = (name, content) => {
    // Try to find an existing tag
    let tag = document.querySelector(`meta[${name.includes(':') ? 'property' : 'name'}="${name}"]`)
    
    // Create the tag if it doesn't exist
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute(name.includes(':') ? 'property' : 'name', name)
      document.head.appendChild(tag)
    }
    
    // Set the content
    tag.setAttribute('content', content)
  }

  // Set basic meta tags
  setMetaTag('description', meta.description)
  setMetaTag('keywords', meta.keywords)
  
  // Set OpenGraph tags
  setMetaTag('og:title', meta.title)
  setMetaTag('og:description', meta.description)
  setMetaTag('og:url', meta.url)
  setMetaTag('og:image', meta.image)
  setMetaTag('og:type', meta.type)
  setMetaTag('og:site_name', meta.siteName)
  
  // Set Twitter Card tags
  setMetaTag('twitter:card', 'summary_large_image')
  setMetaTag('twitter:title', meta.title)
  setMetaTag('twitter:description', meta.description)
  setMetaTag('twitter:image', meta.image)
  
  // Set canonical URL
  let canonicalTag = document.querySelector('link[rel="canonical"]')
  if (!canonicalTag) {
    canonicalTag = document.createElement('link')
    canonicalTag.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalTag)
  }
  canonicalTag.setAttribute('href', meta.url)
}

/**
 * Generate JSON-LD structured data for a service
 * 
 * @param {Object} service - Service information
 * @returns {Object} - JSON-LD structured data
 */
export const serviceStructuredData = (service) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": "AtOnline",
      "url": window.location.origin
    }
  }
}

/**
 * Generate JSON-LD structured data for a list of services
 * 
 * @param {Array} services - Array of service objects
 * @returns {string} - JSON-LD script tag HTML
 */
export const generateServicesStructuredData = (services) => {
  const structuredData = services.map(service => serviceStructuredData(service))
  return `<script type="application/ld+json">${JSON.stringify(structuredData)}</script>`
}

/**
 * Add structured data to page
 * 
 * @param {Object} data - Structured data object
 */
export const addStructuredData = (data) => {
  // Remove any existing structured data
  const existingScript = document.querySelector('script[data-structured-data]')
  if (existingScript) {
    existingScript.remove()
  }
  
  // Create new script element
  const script = document.createElement('script')
  script.setAttribute('type', 'application/ld+json')
  script.setAttribute('data-structured-data', 'true')
  script.textContent = JSON.stringify(data)
  
  // Add to document
  document.head.appendChild(script)
}

// Export default utility
export default {
  updateMetaTags,
  serviceStructuredData,
  generateServicesStructuredData,
  addStructuredData
}